import { useSyncExternalStore } from "react";

/**
 * A single shared "current time" clock backed by one module-level interval.
 * Ported verbatim from nazare-frontend's `screens/admin/workflows/use-now.ts`.
 *
 * Ticking/shared state lives in a module store read via
 * `useSyncExternalStore`, not a per-component `setInterval` inside
 * `useEffect` — any number of components share the one interval below, which
 * only runs while something is subscribed and stops when the last
 * subscriber unmounts. Drives the live elapsed timers on the trace Gantt and
 * run header without a timer-per-row.
 */
const TICK_MS = 1000;

let now = Date.now();
let interval: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function start(): void {
  if (interval !== null) return;
  interval = setInterval(() => {
    now = Date.now();
    for (const listener of listeners) listener();
  }, TICK_MS);
}

function stop(): void {
  if (interval === null) return;
  clearInterval(interval);
  interval = null;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  now = Date.now();
  start();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) stop();
  };
}

function getSnapshot(): number {
  return now;
}

/** Current epoch-ms, re-rendering subscribers about once per second. */
export function useNow(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
